/*
  A/B test "test-cart-004" — cart drawer deal progress bar + offer picker.

  Drives the client-side half of snippets/cart-deal-004.liquid:
    - opening/closing the offer select (desktop dropdown, mobile bottom sheet)
    - picking an offer product (stage 2 -> stage 3), which only fills the
      trigger and reveals the add button; nothing is added yet
    - adding the picked product with the `_deal_item` line item property, which
      re-renders the drawer into stage 4
    - the "How does the offer work?" overlay

  The element re-initialises on every drawer re-render because the mini-cart
  section replaces its own innerHTML, which re-runs connectedCallback().
*/

class CartDeal004 extends HTMLElement {
  constructor() {
    super();

    this.selected = null;
    this.initialised = false;
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);

    // Guard against a second connect (e.g. the element being re-appended)
    // double-binding every child listener.
    if (this.initialised) return;
    this.initialised = true;

    this.select = this.querySelector('[data-deal-select]');
    this.trigger = this.querySelector('[data-deal-trigger]');
    this.panel = this.querySelector('[data-deal-panel]');
    this.backdrop = this.querySelector('.cart-deal__sheet-backdrop');
    this.addButton = this.querySelector('[data-deal-add]');
    this.modal = this.querySelector('[data-deal-modal]');
    this.message = this.querySelector('[data-deal-message]');
    this.progress = this.querySelector('[data-deal-progress]');

    this.resolveCurrency();
    this.autoRemoveExpiredDeal();

    if (this.trigger) {
      this.trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.togglePanel();
      });
    }

    this.querySelectorAll('[data-deal-select-close]').forEach((element) => {
      element.addEventListener('click', (event) => {
        event.preventDefault();
        this.closePanel();
      });
    });

    this.querySelectorAll('[data-deal-option]').forEach((option) => {
      option.addEventListener('click', (event) => {
        event.preventDefault();
        this.selectOption(option);
      });
    });

    if (this.addButton) {
      this.addButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.addSelectedToCart();
      });
    }

    this.querySelectorAll('[data-deal-how-open]').forEach((element) => {
      element.addEventListener('click', (event) => {
        event.preventDefault();
        this.openModal();
      });
    });

    this.querySelectorAll('[data-deal-how-close]').forEach((element) => {
      element.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeModal();
      });
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  /* ---------------------------------------------------------------- */
  /* Offer select                                                     */
  /* ---------------------------------------------------------------- */

  get panelOpen() {
    return !!this.panel && !this.panel.hasAttribute('hidden');
  }

  togglePanel() {
    if (this.panelOpen) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  openPanel() {
    if (!this.panel) return;

    this.panel.removeAttribute('hidden');
    if (this.backdrop) this.backdrop.removeAttribute('hidden');
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
  }

  closePanel() {
    if (!this.panel) return;

    this.panel.setAttribute('hidden', '');
    if (this.backdrop) this.backdrop.setAttribute('hidden', '');
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
  }

  selectOption(option) {
    if (option.hasAttribute('disabled')) return;

    this.selected = option;

    this.querySelectorAll('[data-deal-option]').forEach((element) => {
      element.setAttribute('aria-selected', String(element === option));
    });

    // The trigger and every option share the same `.cart-deal__row` markup, so
    // the picked row can simply be cloned in — image, title, prices and the
    // saving pill all land exactly as the design has them (Figma 18604:3423).
    const triggerRow = this.querySelector('[data-deal-trigger-row]');
    const optionRow = option.querySelector('.cart-deal__row');
    if (triggerRow && optionRow) {
      triggerRow.replaceChildren(...Array.from(optionRow.cloneNode(true).childNodes));
    }

    if (this.addButton) this.addButton.removeAttribute('hidden');

    this.closePanel();
  }

  /* ---------------------------------------------------------------- */
  /* Currency                                                         */
  /* ---------------------------------------------------------------- */

  /*
    The unlock threshold is a merchant-entered amount in the shop's currency,
    but `cart.total_price` is in the presentment currency — so the threshold has
    to travel at the market's exchange rate, exactly the way Shopify adapts a
    free-shipping threshold to other currencies.

    That rate only exists client-side (`Shopify.currency.rate`), so the server
    renders at rate 1 and this re-resolves the threshold, the stage, the
    progress and the copy. On the shop's own currency the rate is 1 and this is
    a no-op, so the common case never repaints.
  */
  resolveCurrency() {
    const thresholdBase = parseInt(this.dataset.thresholdBase, 10);
    const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);

    this.rate = Number.isFinite(rate) && rate > 0 ? rate : 1;
    this.qualifyingTotal = parseInt(this.dataset.qualifyingTotal, 10) || 0;
    this.threshold = Number.isFinite(thresholdBase) ? Math.round(thresholdBase * this.rate) : 0;
    this.dealKeys = (this.dataset.dealKeys || '')
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean);
    this.autoRemove = this.dataset.autoRemove === 'true';

    this.qualifies = this.threshold <= 0 || this.qualifyingTotal >= this.threshold;
    this.expired = this.dealKeys.length > 0 && this.autoRemove && !this.qualifies;

    // Anything the server could not have known only applies off the base rate.
    if (this.rate === 1) return;

    let state;
    if (this.dealKeys.length && !this.expired) {
      state = 'added';
    } else if (this.qualifies) {
      state = 'unlocked';
    } else {
      state = 'locked';
    }

    this.dataset.state = state;
    this.renderProgress();
    this.renderMessage(state);
  }

  renderProgress() {
    if (!this.progress) return;

    const ratio = this.threshold > 0 ? Math.min(this.qualifyingTotal / this.threshold, 1) : 1;
    const percent = Math.round(ratio * 100);

    this.progress.style.setProperty('--cart-deal-progress', `${percent}%`);
    this.progress.setAttribute('aria-valuenow', String(percent));
  }

  renderMessage(state) {
    if (!this.message) return;

    const templates = {
      locked: this.dataset.lockedText,
      unlocked: this.dataset.unlockedText,
      added: this.dataset.addedText,
    };
    const template = templates[state];
    if (!template) return;

    const values = {
      '{amount}': this.formatMoney(Math.max(this.threshold - this.qualifyingTotal, 0)),
      '{threshold}': this.formatMoney(this.threshold),
      '{discount}': this.dataset.discountLabel || '',
    };

    // Built as nodes rather than innerHTML so the substituted values keep the
    // same <strong> wrapper the Liquid emits without re-parsing merchant copy.
    this.message.replaceChildren();
    template.split(/(\{amount\}|\{threshold\}|\{discount\})/).forEach((part) => {
      if (!part) return;

      if (Object.prototype.hasOwnProperty.call(values, part)) {
        const strong = document.createElement('strong');
        strong.className = 'cart-deal__hl';
        strong.textContent = values[part];
        this.message.appendChild(strong);
      } else {
        this.message.appendChild(document.createTextNode(part));
      }
    });
  }

  /*
    Learns the active currency's formatting from a Liquid-rendered sample of a
    known amount (123456 cents). The theme's own `theme.Currency.formatMoney`
    is keyed to `shop.money_format`, which does not follow the presentment
    currency; reading back what the `money` filter actually produced does.
  */
  get moneyFormat() {
    if (this.moneyFormatCache !== undefined) return this.moneyFormatCache;

    this.moneyFormatCache = null;

    const sample = this.querySelector('[data-deal-money-sample]');
    const text = sample ? sample.textContent.trim() : '';
    const match = text.match(/\d[\d.,'    ]*\d|\d/);

    if (match) {
      const separators = match[0].replace(/\d/g, '');
      // 123456 cents renders as 1234.56 with decimals, or 1235 without.
      const decimals = match[0].replace(/\D/g, '').length >= 6 ? 2 : 0;

      this.moneyFormatCache = {
        prefix: text.slice(0, match.index),
        suffix: text.slice(match.index + match[0].length),
        decimals: decimals,
        decimal: decimals ? separators.slice(-1) : '',
        thousands: decimals ? (separators.length > 1 ? separators.charAt(0) : '') : separators.slice(-1),
      };
    }

    return this.moneyFormatCache;
  }

  formatMoney(cents) {
    const format = this.moneyFormat;
    if (!format) return '';

    const parts = (Math.round(cents) / 100).toFixed(format.decimals).split('.');
    const whole = format.thousands
      ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, format.thousands)
      : parts[0];
    const fraction = parts[1] ? format.decimal + parts[1] : '';

    return format.prefix + whole + fraction + format.suffix;
  }

  /* ---------------------------------------------------------------- */
  /* Losing the offer                                                 */
  /* ---------------------------------------------------------------- */

  /*
    When the rest of the cart stops reaching the threshold (a quantity was
    lowered, another line was removed, ...) the claimed offer is no longer
    earned. Liquid cannot mutate the cart, so the removal happens here, on the
    render that first detects it — against the rate-converted threshold
    resolved in `resolveCurrency()`, so it behaves the same in every currency.

    The drawer is re-rendered by hand rather than through
    `mini-cart.renderContents()` because that helper also opens the drawer, and
    this runs unprompted — it must never pop the cart open by itself.
  */
  autoRemoveExpiredDeal() {
    if (!this.expired) return;

    const keys = this.dealKeys;

    // A re-render mid-flight would otherwise fire a second removal.
    if (CartDeal004.removing) return;
    CartDeal004.removing = true;

    const miniCart = document.querySelector('mini-cart');
    const sectionsToRender = miniCart ? miniCart.getSectionsToRender() : [];

    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });

    const body = JSON.stringify({
      updates: updates,
      sections: sectionsToRender.map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${window.routes.cart_update_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        sectionsToRender.forEach((section) => {
          const element = document.getElementById(section.id);
          if (!element || !parsedState.sections || !parsedState.sections[section.id]) return;
          element.innerHTML = miniCart.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        });

        if (miniCart) {
          miniCart.initializeMiniCartReviews();
          miniCart.initializeTimer();
        }

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: state } }));
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        CartDeal004.removing = false;
      });
  }

  /* ---------------------------------------------------------------- */
  /* Claiming the offer                                               */
  /* ---------------------------------------------------------------- */

  addSelectedToCart() {
    if (!this.selected || !this.addButton) return;

    const variantId = this.selected.dataset.variantId;
    if (!variantId) return;

    const miniCart = document.querySelector('mini-cart');
    const sections = miniCart ? miniCart.getSectionsToRender().map((section) => section.id) : [];

    this.addButton.classList.add('is-loading');
    this.addButton.setAttribute('disabled', 'disabled');

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      properties: {
        _deal_item: 'true',
      },
      sections: sections,
      sections_url: window.location.pathname,
    });

    fetch(`${window.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status === 422) {
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:error', {
              detail: { errorMessage: parsedState.description },
            })
          );
          return;
        }

        if (miniCart) miniCart.renderContents(parsedState);

        document.dispatchEvent(
          new CustomEvent('ajaxProduct:added', {
            detail: { product: parsedState },
          })
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.addButton.classList.remove('is-loading');
        this.addButton.removeAttribute('disabled');
      });
  }

  /* ---------------------------------------------------------------- */
  /* "How does the offer work?" overlay                               */
  /* ---------------------------------------------------------------- */

  get modalOpen() {
    return !!this.modal && !this.modal.hasAttribute('hidden');
  }

  openModal() {
    if (!this.modal) return;

    this.closePanel();
    this.modal.removeAttribute('hidden');

    const close = this.modal.querySelector('.cart-deal__modal-close');
    if (close) close.focus();
  }

  closeModal() {
    if (!this.modal) return;

    this.modal.setAttribute('hidden', '');
  }

  /* ---------------------------------------------------------------- */
  /* Dismissal                                                        */
  /* ---------------------------------------------------------------- */

  onDocumentClick(event) {
    if (!this.panelOpen || !this.select) return;
    if (this.select.contains(event.target)) return;

    this.closePanel();
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;

    if (this.modalOpen) {
      event.stopPropagation();
      this.closeModal();
    } else if (this.panelOpen) {
      event.stopPropagation();
      this.closePanel();
    }
  }
}

if (!customElements.get('cart-deal-004')) {
  customElements.define('cart-deal-004', CartDeal004);
}
